import { observer } from 'mobx-react';
import { useOpenable } from '@execonline-inc/execonline-ui';
import type { JSX } from 'react';
import { ExperienceResourceArray, isSystemManaged, ProductCollectionResource } from '../Types';
import Carousel from './DisplayTypes/Carousel';
import Grid from './DisplayTypes/Grid';
import List from './DisplayTypes/List';
import ModalWithExperiences from './AddPrograms/ModalWithExperiences';
import SystemManagedSection from './SystemManagedSection';

export interface Props {
  productCollection: ProductCollectionResource;
  experiencesArray: ExperienceResourceArray;
  index: number;
  totalSections: number;
  editable?: boolean;
}

function Section({
  productCollection,
  experiencesArray,
  index,
  totalSections,
  editable = false,
}: Props) {
  const { onOpen, isOpen, onClose } = useOpenable();

  // Early return for system-managed sections
  if (isSystemManaged(productCollection.payload.kind)) {
    return <SystemManagedSection productCollection={productCollection} index={index} />;
  }

  const displayTypeComponent: JSX.Element = (() => {
    switch (productCollection.payload.display_type) {
      case 'carousel':
        return (
          <Carousel
            productCollection={productCollection}
            experiencesArray={experiencesArray}
            index={index}
            totalSections={totalSections}
            onOpenCatalog={onOpen}
            editable={editable}
          />
        );
      case 'list':
        return (
          <List
            productCollection={productCollection}
            experiencesArray={experiencesArray}
            index={index}
            totalSections={totalSections}
            onOpenCatalog={onOpen}
            editable={editable}
          />
        );
      case 'grid':
        return (
          <Grid
            productCollection={productCollection}
            experiencesArray={experiencesArray}
            index={index}
            totalSections={totalSections}
            onOpenCatalog={onOpen}
            editable={editable}
          />
        );
      default: {
        // Log unexpected display_type for debugging
        const unexpectedType: never = productCollection.payload.display_type;
        console.error(`[Section] Unknown display_type: ${unexpectedType}`);
        // Fallback to grid
        return (
          <Grid
            productCollection={productCollection}
            experiencesArray={experiencesArray}
            index={index}
            totalSections={totalSections}
            onOpenCatalog={onOpen}
            editable={editable}
          />
        );
      }
    }
  })();

  return (
    <>
      {displayTypeComponent}
      <ModalWithExperiences
        productCollection={productCollection}
        index={index}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
}

export default observer(Section);
